import QRCode from "qrcode";
import {
  MARGEN_QR_MODULOS,
  cabeceraNumeroPedido,
  calcularLadoQrMm,
  envolverDocumento,
  escaparHtml,
  formatearFecha,
  formatearFechaAfip,
  formatearFechaHora,
  formatearPesos,
} from "./html";

// Primer papel de la venta. Todo sale del DTO autoritativo: la factura muestra
// lo que AFIP dejó en Venta y la constancia lo que quedó en Pedido. Ninguno
// recalcula IVA ni inventa un comprobante.

const limpiar = (valor) => String(valor || "").trim();
const unir = (partes, separador = " · ") => partes.filter(Boolean).join(separador);

// Todo lo que exige AFIP del emisor, en renglones que no parten frases: el
// domicilio solo (es el de largo variable), condición IVA con inicio de
// actividades, y CUIT con IIBB.
const bloqueEmisor = (emisor = {}) => {
  const nombre = limpiar(emisor?.nombre) || limpiar(emisor?.razonSocial);
  const razonSocial =
    limpiar(emisor?.razonSocial) && limpiar(emisor.razonSocial) !== nombre ? emisor.razonSocial : "";
  const lineas = [
    razonSocial,
    emisor?.domicilio,
    unir([
      emisor?.condicionIva,
      emisor?.inicioActividades ? `Inicio act. ${formatearFecha(emisor.inicioActividades)}` : "",
    ]),
    unir([
      emisor?.cuit ? `CUIT ${emisor.cuit}` : "",
      emisor?.ingresosBrutos ? `IIBB ${emisor.ingresosBrutos}` : "",
    ]),
  ].filter(Boolean);
  return `<h1>${escaparHtml(nombre)}</h1>${lineas
    .map((linea) => `<p class="centro chico">${escaparHtml(linea)}</p>`)
    .join("")}`;
};

// Sin fila de encabezado: cantidad, nombre e importe se explican solos. El
// unitario sólo aparece cuando la cantidad es mayor que 1; con una unidad el
// importe ya lo dice.
const tablaLineas = (lineas = [], { conPrecios }) => {
  const filas = lineas
    .map((linea) => {
      const cantidad = Number(linea.cantidad || 0);
      const observacion = linea.observacion
        ? `<tr><td colspan="${conPrecios ? 3 : 2}" class="obs">${escaparHtml(linea.observacion)}</td></tr>`
        : "";
      const unitario =
        conPrecios && cantidad > 1
          ? `<br><span class="unitario">${escaparHtml(cantidad)} × ${escaparHtml(formatearPesos(linea.precioUnitario))}</span>`
          : "";
      const celdas = conPrecios
        ? `<td class="cant">${escaparHtml(cantidad)}</td><td>${escaparHtml(linea.nombre)}${unitario}</td><td class="num">${escaparHtml(formatearPesos(linea.subtotal))}</td>`
        : `<td class="cant">${escaparHtml(cantidad)}</td><td>${escaparHtml(linea.nombre)}</td>`;
      return `<tr>${celdas}</tr>${observacion}`;
    })
    .join("");
  return `<table>${filas}</table>`;
};

const filaImporte = (etiqueta, valor, clase = "") =>
  `<div class="fila ${clase}"><span>${escaparHtml(etiqueta)}</span><span>${escaparHtml(formatearPesos(valor))}</span></div>`;

const bloqueTotales = (pedido = {}) => {
  const descuento = Number(pedido.descuento || 0);
  return [
    descuento > 0 ? filaImporte("Subtotal", pedido.subtotal) : "",
    descuento > 0 ? filaImporte("Descuento", -descuento) : "",
    descuento > 0 && pedido.voucher
      ? `<p class="chico">Voucher: ${escaparHtml(pedido.voucher)}</p>`
      : "",
    filaImporte("TOTAL", pedido.total, "negrita"),
  ].join("");
};

// AFIP identifica el comprobante como PPPPP-NNNNNNNN. Si el número ya viene
// compuesto se respeta tal cual.
export const formatearNumeroComprobante = (puntoVenta, numero) => {
  const texto = String(numero ?? "").trim();
  if (!texto) return "";
  if (texto.includes("-") || !/^\d+$/.test(texto)) return texto;
  const punto = String(puntoVenta ?? "").trim();
  if (!/^\d+$/.test(punto)) return texto.padStart(8, "0");
  return `${punto.padStart(5, "0")}-${texto.padStart(8, "0")}`;
};

const tituloFactura = (factura = {}) => {
  const tipo = String(factura.tipo || "").trim().toUpperCase();
  return tipo ? `FACTURA ${tipo}` : "FACTURA";
};

const bloqueReceptor = (factura = {}) => {
  const documento =
    factura.numeroDocumento && factura.numeroDocumento !== "0"
      ? `${factura.tipoDocumentoNombre || "Doc."} ${factura.numeroDocumento}`
      : "";
  return `<p><span class="negrita">Cliente:</span> ${escaparHtml(
    unir([factura.cliente || "Consumidor final", documento])
  )}</p>`;
};

// Ley 27.743 exige la leyenda y el IVA contenido; van juntos en un renglón.
// CAE y vencimiento en otro.
const bloqueFiscal = (factura = {}) => {
  const lineas = [
    factura.importeIva !== null && factura.importeIva !== undefined
      ? `Régimen de Transparencia Fiscal al Consumidor (Ley 27.743) · IVA contenido ${formatearPesos(factura.importeIva)}`
      : "",
    unir([
      factura.cae ? `CAE ${factura.cae}` : "",
      factura.caeVencimiento ? `Vto. ${formatearFechaAfip(factura.caeVencimiento)}` : "",
    ]),
  ].filter(Boolean);
  return lineas.map((linea) => `<p class="chico">${escaparHtml(linea)}</p>`).join("");
};

// El viewBox del SVG de qrcode es "0 0 N N", con N = módulos + zona de
// silencio: es la cantidad real de módulos de este comprobante, que varía con
// el largo de la URL de AFIP.
const modulosDelSvg = (svg) => {
  const partes = String(svg).match(/viewBox="0 0 (\d+) (\d+)"/);
  return partes ? Number(partes[1]) : null;
};

// SVG inline y no PNG: se rasteriza nítido a la resolución de la impresora en
// lugar de reescalar un mapa de bits. Nivel L porque el contenido AFIP (~300
// bytes) ya es grande: con M sube dos versiones y los módulos se achican. La
// zona de silencio de 4 módulos es obligatoria para que los lectores lo tomen.
// El lado se calcula para que cada módulo sean puntos enteros del cabezal.
const generarQr = async (contenido) => {
  if (!contenido) return null;
  try {
    const svg = await QRCode.toString(contenido, {
      type: "svg",
      errorCorrectionLevel: "L",
      margin: MARGEN_QR_MODULOS,
    });
    const modulos = modulosDelSvg(svg);
    if (!modulos) return null;
    return { svg, ladoMm: calcularLadoQrMm(modulos) };
  } catch (error) {
    // Sin QR se imprime igual: el comprobante sigue siendo válido y el CAE va
    // en texto.
    return null;
  }
};

// Factura fiscal. Sólo debe invocarse con estadoFiscal FACTURADA: sin
// comprobante persistido no hay nada legítimo que dibujar.
export const renderFactura80mm = async (documentos) => {
  const { emisor, factura, pedido, lineas } = documentos || {};
  if (!factura) {
    throw new Error("La operación no tiene un comprobante fiscal persistido");
  }
  const qr = await generarQr(factura.qr);
  const numero = formatearNumeroComprobante(factura.puntoVenta, factura.numero);
  // El punto de venta ya está dentro del número; no se repite.
  const cuerpo = `
    ${bloqueEmisor(emisor)}
    <div class="sep"></div>
    ${cabeceraNumeroPedido(pedido)}
    <p class="centro"><span class="negrita">${escaparHtml(tituloFactura(factura))}</span> Nro. ${escaparHtml(numero)}</p>
    <p class="centro chico">${escaparHtml(formatearFecha(factura.fecha))}</p>
    <div class="sep"></div>
    ${bloqueReceptor(factura)}
    <div class="sep"></div>
    ${tablaLineas(lineas, { conPrecios: true })}
    <div class="sep"></div>
    ${bloqueTotales(pedido)}
    <div class="sep"></div>
    ${bloqueFiscal(factura)}
    ${
      qr
        ? `<div class="qr" role="img" aria-label="QR AFIP" style="width:${qr.ladoMm}mm;height:${qr.ladoMm}mm">${qr.svg}</div>`
        : ""
    }
  `;
  return envolverDocumento({
    titulo: `${tituloFactura(factura)} ${numero}`.trim(),
    cuerpo,
  });
};

// Total cero cubierto por voucher: la política vigente no factura y este papel
// lo dice de forma dominante. No lleva tipo de comprobante, CAE ni QR.
export const renderConstanciaBonificada80mm = (documentos) => {
  const { emisor, pedido, lineas } = documentos || {};
  const cuerpo = `
    <h1>${escaparHtml(emisor?.nombre || emisor?.razonSocial || "")}</h1>
    <div class="aviso">OPERACIÓN BONIFICADA<br>NO VÁLIDA COMO FACTURA</div>
    ${cabeceraNumeroPedido(pedido)}
    <p class="centro chico">${escaparHtml(unir([formatearFechaHora(pedido?.fecha), pedido?.evento]))}</p>
    <div class="sep"></div>
    ${tablaLineas(lineas, { conPrecios: true })}
    <div class="sep"></div>
    ${bloqueTotales(pedido)}
    <p class="centro chico">Documento no fiscal: la operación quedó cubierta por un voucher.</p>
  `;
  return envolverDocumento({
    titulo: `Constancia bonificada ${pedido?.nroPedido || pedido?.idPedido || ""}`.trim(),
    cuerpo,
  });
};
